import type { IProducerFicEntityListResponse } from 'src/types/producer-fic-entity';
import type { IProducerCompany360, IProducerCompanyListResponse } from 'src/types/producer-company';
// utils
// axiosInstance
import axiosInstance, { endpoints, endpointsLegacy } from 'src/utils/axios';
import type { IAssistantMetricsResponse } from 'src/types/assistant-metrics';
import type { ReorderSheet, ReorderSheetStatus } from 'src/types/reorder-sheet';
import type {
  IStockProducersPage,
  IStockQualityListParams,
  IStockQualitySummary,
  IStockWinesPage,
  StockProducerSortKey,
  StockQualityFilterParams,
  StockWineSortKey,
} from 'src/types/stock-quality';
import type {
  IArchiveHold,
  IArchiveHoldAllocatePayload,
  IArchiveHoldAllocateResult,
  IArchiveHoldListParams,
  IArchiveHoldListResponse,
  IArchiveHoldReleasePayload,
  IArchiveHoldReleaseResult,
  IArchiveHoldUpsertPayload,
  IProductArchiveHold,
} from 'src/types/archive-hold';
import type {
  IReceivablesPosition,
  ISlowMoversPage,
  ISlowMoversParams,
  IStockPosition,
} from 'src/types/positions';
import type {
  IRetentionList,
  IRetentionListParams,
  IRetentionOwnerCandidate,
  IRetentionUpdate,
} from 'src/types/client-retention';
import type {
  IAssistantConversationDetail,
  IAssistantConversationList,
  IAssistantUnservedList,
} from 'src/types/assistant-review';
import { REQUESTED_METRICS } from 'src/sections/admin/assistant/metrics-registry';
// types
import {
  IListFilters,
  APIListResponse,
  IFetchBaseParams,
  IClientRegions,
  IClientRegionsAndProvinces,
  IClientTableFilters,
  IClientItem,
  IReservationItem,
  IUserItem,
  IAgentItem,
  IAgentStatement,
  IAgentSummary,
  IAgentsTableFilters,
  IUserTableFilters,
  IReservationsTableFilters,
  IProductTableFilters,
  IOrderProductItem,
  IProducerTableFilters,
  IProductItem,
  IProducerItem,
  IProducerInstagram,
  IClientOrderTableFilters,
  IClientOrderItem,
  ClientOrderedProducts,
  IProducerOrderItem,
  IProducerOrderTableFilters,
  IProductStatsFilters,
  IProductStatsItem,
  IProfitabilityStatsFilters,
  IBestSellingProductsStatsFilter,
  IProfitabilityItem,
  IUpdateLogisticsCost,
  IRecalculateStatistics,
  IImportProductItem,
  INotifyStorageAboutProducerOrder,
  IProducerOrderProducts,
  INotifyStorageAboutClientOrder,
  IAddAvailabilityToProducerOrder,
  IWineTypeItem,
  ITastingItem,
  ITastingListParams,
  ITastingListRow,
  ITastingFormValues,
  ITastingClient,
  ITastingProduct,
  ISalesStatisticsRegionwise,
  ISalesStatistics,
  IBestSalesStatistics,
  IClientStatDetailsItem,
  IClientDetailedStatsItem,
  IClientYearlySummariesItem,
  IProfitabilityRealTimeFilters,
  YearlyStat,
  IWineOrderDetails,
  IWineOrderDetailFilters,
  IReservations,
  IAnnualStatParams,
  IProductGeneralStatItem,
  IProductCustomerOrderItem,
  IProductGeographicalChartItem,
  IClientOrderFrequencyChartItem,
  IClientRadarChartItem,
  IClientPriceDistChartItem,
  IOverlayItem,
  ILogisticsStats,
  IOrderStats,
  ICurrentMonthStats,
  DashboardPeriod,
  ICustomerOrders,
  commissionItem,
  commissionItemData,
  WineRevenueStatistics,
  IUserOrderDetails,
  IServiceZoneItem,
  IStockAnalysisOptimizationItem,
  ICurrentQuarterStats,
  IProductPerformanceFilters,
  IProductPerformanceStats,
  IProducerOrderPaymentDetails,
  ITerritoryItem,
  ITerritoryResponse,
  ITerritoryTableFilters,
  IProducerRegionItem,
  IAnalyticsFiltersParams,
  IPriceDistChartItem,
  IRadarChartItem,
  ITargetItem,
  IStockOptimizationStat,
  IProducerClientProvinceExclusionsParams,
  IPriceRangeStockBehaviourStats,
  IPriceRangeStockBehaviourFilters,
  ICountriesResponse,
  IWineCategoriesResponse,
  IFlowAnalysisFilters,
  IFlowAnalysisStats,
  IWineCategoryMetaCategory,
  IYearlyTrends,
  IDiscounts,
  IProducerRiskItem,
  ICustomerDiscount,
  IGrapeTypeItem,
  IDiscountsTableFilters,
  IDiscountItem,
  IOrderPlacedByStatistics,
  IProducerStockItem,
  IProducerPaymentCreate,
  IProducerPayment,
  ProducerDueItem,
  ICountryItem,
  IAgingReceivables,
  ICollectionActivity,
  ICreateCollectionActivity,
  IAgingPayables,
  ICatalogPdfJob,
  IAgentServiceZone,
  ISelectionItem,
  ICompanyListResponse,
  ICompany360,
  ICustomer360,
  IUserGrantsResponse,
  ICompanyVenueSuggestionsResponse,
} from 'src/types';
import {
  IFinancialResponse,
  IIncomingOutgoingCashflowItem,
  FinancialOverviewApiResponse,
  DailyKpiSummary,
} from 'src/types/financials';
import { ITableTextItem, ITableExplanation, ITableTextParams } from 'src/types/table-text';

import useGetData from 'src/hooks/use-get-data';
import { ICourier } from 'src/types/courier';

const isLegacy = {
  isLegacy: true,
};

export function useGetClientOrderList(params?: IListFilters<IClientOrderTableFilters>) {
  return useGetData<IListFilters<IClientOrderTableFilters>, APIListResponse<IClientOrderItem[]>>({
    endpoint: endpoints.admin.orders.client_orders,
    params,
  });
}

export function useGetClientOrder(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IClientOrderItem>({
    endpoint: params?.order_code
      ? `${endpoints.admin.orders.client_orders}/${params?.order_code}`
      : null,
  });
}
export async function createClientDiscount(data: ICustomerDiscount) {
  const URL = `${endpoints.admin.orders.client_order_discount}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export function useGetUsersOrder(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IUserOrderDetails>({
    endpoint: `${endpoints.admin.userOrder}/${params?.slug}`,
  });
}

export function useGetProducerOrderList(params?: IListFilters<IProducerOrderTableFilters>) {
  return useGetData<IListFilters<IProducerOrderTableFilters>, APIListResponse<IProducerOrderItem>>({
    endpoint: `${endpoints.admin.orders.producer_orders}`,
    params,
  });
}

export function useGetProducerOrder(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IProducerOrderItem>({
    endpoint: params?.order_code
      ? `${endpoints.admin.orders.producer_orders}/${params?.order_code}`
      : null,
    params,
  });
}

export function useGetProducerOrderPaymentDetails(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IProducerOrderPaymentDetails>({
    endpoint: params?.producer_order_id
      ? `${endpoints.admin.orders.producer_order_payments}/${params?.producer_order_id}`
      : null,
    params,
  });
}

export async function addNewProducerPayment(params?: IFetchBaseParams) {
  const URL = `${endpoints.admin.orders.producer_order_payments}/${params?.producer_order_id}`;
  const res = await axiosInstance.post(URL, params);
  return res;
}

// Delete supplier partial payment
export async function deleteProducerPayment(params?: IFetchBaseParams) {
  const URL = `${endpoints.admin.orders.producer_order_payments}/${params?.id}/delete-payment`;
  await axiosInstance.delete(URL);
}

// `enabled` lets a caller keep the hook in place while skipping the request —
// used by the defect typeahead (NAT-1899), which needs the whole catalogue
// (`active: 'all'`) but only once a line is marked as a defect replacement.
export function useGetProductList(
  params?: IListFilters<IProductTableFilters>,
  enabled: boolean = true,
) {
  // The products endpoint attaches stock summary counters at the top level
  // beside the paginated data — the admin products view renders them in the
  // summary cards above the table.
  return useGetData<
    IListFilters<IProductTableFilters>,
    APIListResponse<IOrderProductItem[]> & {
      sku_in_stock?: number;
      storage_quantity?: number;
      available_quantity?: number;
      stock_value?: number;
    }
  >({
    endpoint: enabled ? endpoints.admin.products.root : null,
    params,
  });
}

export function useGetProducerCountries() {
  return useGetData<Record<string, never>, string[]>({
    endpoint: endpoints.admin.products.producerCountries,
  });
}

export function useGetProduct(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IProductItem>({
    endpoint: params?.sku ? `${endpoints.admin.products.root}/${params?.sku}` : null,
  });
}

export function useGetProductGeneralStats(params?: IFetchBaseParams) {
  const queryString = new URLSearchParams({
    vintage: params?.vintage?.toString() ?? '',
  }).toString();
  return useGetData<IFetchBaseParams, IProductGeneralStatItem>({
    endpoint:
      params?.sku && params?.tab
        ? `${endpoints.admin.products.root}/${params?.sku}/${params?.tab}?${queryString}`
        : null,
  });
}

export function useGetProductProducerOrders(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IProductCustomerOrderItem[]>({
    endpoint:
      params?.sku && params?.tab
        ? `${endpoints.admin.products.root}/${params?.sku}/${params?.tab}`
        : null,
    params,
  });
}
export function useGetProductCustomerOrders(params?: IFetchBaseParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IFetchBaseParams, IProductCustomerOrderItem[]>({
    endpoint:
      params?.sku && params?.tab
        ? `${endpoints.admin.products.root}/${params?.sku}/${params?.tab}?${queryString}`
        : null,
  });
}

export function useGetGeographicalChart(params?: IFetchBaseParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IFetchBaseParams, IProductGeographicalChartItem[]>({
    endpoint:
      params?.sku && params?.tab
        ? `${endpoints.admin.products.root}/${params?.sku}/${params?.tab}?${queryString}`
        : null,
  });
}

export function useGetWineTypeList(params?: IListFilters<{}>) {
  return useGetData<IListFilters<{}>, IWineTypeItem[]>({
    endpoint: endpoints.admin.wineTypes,
    params,
  });
}

/**
 * @param keepPreviousData hold the rows already on screen while a new filter
 * loads. Every parameter here narrows the same table rather than asking for a
 * different one, so blanking the body mid-change is a worse answer than a
 * moment of slightly stale rows.
 */
export function useGetWineCategoryList(params?: IListFilters<{}>, keepPreviousData = false) {
  return useGetData<IListFilters<{}>, IWineTypeItem[]>({
    endpoint: endpoints.admin.wineListing,
    params,
    keepPreviousData,
  });
}

export function useGetWineType(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IWineTypeItem>({
    endpoint: params?.id ? `${endpoints.admin.wineTypes}/${params?.id}` : null,
  });
}

export function useGetGrapeTypeList(params?: IListFilters<{}>) {
  return useGetData<IListFilters<{}>, IGrapeTypeItem[]>({
    endpoint: endpoints.admin.grapeTypes,
    params,
  });
}

export function useGetGrapeType(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IGrapeTypeItem>({
    endpoint: params?.id ? `${endpoints.admin.grapeTypes}/${params?.id}` : null,
    params,
  });
}

export function useGetWineCategoryMetaCategoryList() {
  return useGetData<IListFilters<{}>, IWineCategoryMetaCategory>({
    endpoint: `${endpoints.admin.wineCategories}`,
  });
}

export function useGetTastingsList(params?: ITastingListParams) {
  return useGetData<ITastingListParams, ITastingListRow[]>({
    endpoint: endpoints.admin.tastings,
    params,
    keepPreviousData: true,
  });
}

export function useGetTastings(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, ITastingItem>({
    endpoint: params?.slug ? `${endpoints.admin.tastings}/${params?.slug}` : null,
  });
}

export function useGetGrapeTypes() {
  return useGetData<undefined, { grape_types: string[] }>({
    endpoint: endpoints.admin.products.grapeTypes,
  });
}

export function useGetProducerList(params?: IListFilters<IProducerTableFilters>) {
  return useGetData<IListFilters<IProducerTableFilters>, APIListResponse<IProducerItem[]>>({
    endpoint: endpoints.admin.producers,
    params,
  });
}

export function useGetProducer(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IProducerItem>({
    endpoint: params?.slug ? `${endpoints.admin.producers}/${params?.slug}` : null,
    params,
  });
}

export function useGetProducerGeneralStats(params?: IFetchBaseParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IFetchBaseParams, any>({
    endpoint: params?.slug
      ? `${endpoints.admin.producer}/${params?.slug}/stats?${queryString}`
      : null,
  });
}

export function useGetUserList(params?: IListFilters<IUserTableFilters>) {
  return useGetData<IListFilters<IUserTableFilters>, APIListResponse<IUserItem[]>>({
    endpoint: endpoints.admin.users,
    params,
  });
}

export function useGetUser(params: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IUserItem>({
    endpoint: `${endpoints.admin.users}/${params.slug}`,
    params,
  });
}

// NAT-2499 (phase 6) — a person's real, current grants. Seeds the admin link-management
// form's multi-select instead of inferring selections from the list view's comma-joined
// `client_name` string, which cannot be split back into slugs.
export function useGetUserGrants(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IUserGrantsResponse>({
    endpoint: params?.slug ? endpoints.admin.userGrants(params.slug) : null,
  });
}

// NAT-2499 (phase 6) — venues ever associated with the same company as the one just
// linked, offered as a suggestion. A suggestion only, and not a claim of current
// ownership — see ICompanyVenueSuggestionsResponse's own doc.
export function useGetCompanyVenueSuggestions(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, ICompanyVenueSuggestionsResponse>({
    endpoint: params?.clientId ? endpoints.admin.clientCompanyVenues(params.clientId) : null,
  });
}

export function useGetClientList(
  params?: IListFilters<IClientTableFilters>,
  enabled: boolean = true,
) {
  return useGetData<IListFilters<IClientTableFilters>, APIListResponse<IClientItem[]>>({
    endpoint: enabled ? endpoints.admin.clients : null,
    params,
  });
}

export function useGetClient(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IClientItem>({
    endpoint: params?.slug ? `${endpoints.admin.clients}/${params?.slug}/new` : null,
  });
}

export function useGetWineOrdersDetails(slug: string, params?: IWineOrderDetailFilters) {
  return useGetData<IWineOrderDetailFilters, IWineOrderDetails[]>({
    endpoint: slug ? `${endpoints.admin.clients}/${slug}/wineOrders` : null,
    params,
  });
}

export function useGetClientStatDetails(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IClientStatDetailsItem>({
    endpoint:
      params?.slug && params?.tab
        ? `${endpoints.admin.clients}/${params?.slug}/${params?.tab}`
        : null,
  });
}
export function useGetClientReservationDetails(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IReservations[]>({
    endpoint:
      params?.slug && params?.tab
        ? `${endpoints.admin.clients}/${params?.slug}/${params?.tab}`
        : null,
  });
}

export function useGetClientDiscountDetails(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IDiscounts[]>({
    endpoint:
      params?.slug && params?.tab
        ? `${endpoints.admin.clients}/${params?.slug}/${params?.tab}`
        : null,
  });
}

export function useGetClientDetailedStatsDetails(params?: IFetchBaseParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IFetchBaseParams, IClientDetailedStatsItem>({
    endpoint:
      params?.slug && params?.tab && params?.filter
        ? `${endpoints.admin.clients}/${params?.slug}/${params?.tab}?${queryString}`
        : null,
  });
}

export function useGetClientYearlySummariesDetails(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IClientYearlySummariesItem>({
    endpoint:
      params?.slug && params?.tab
        ? `${endpoints.admin.clients}/${params?.slug}/${params?.tab}`
        : null,
  });
}

export function useClientOrderFrequencyChartData(params?: IFetchBaseParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IFetchBaseParams, IClientOrderFrequencyChartItem>({
    endpoint:
      params?.slug && params?.tab
        ? `${endpoints.admin.clients}/${params?.slug}/${params?.tab}?${queryString}`
        : null,
  });
}

export function useGetPriceDistributionChartData(params?: IAnalyticsFiltersParams) {
  return useGetData<IAnalyticsFiltersParams, IPriceDistChartItem>({
    endpoint: endpoints.admin.statistics.analytics.priceDistribution,
    params,
  });
}
export function useGetWineDistributionChartData(params?: IAnalyticsFiltersParams) {
  return useGetData<IAnalyticsFiltersParams, IRadarChartItem>({
    endpoint: endpoints.admin.statistics.analytics.wineDistribution,
    params,
  });
}

export function useClientWineDistributionChartData(params?: IFetchBaseParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IFetchBaseParams, IClientRadarChartItem>({
    endpoint:
      params?.slug && params?.tab
        ? `${endpoints.admin.clients}/${params?.slug}/${params?.tab}?${queryString}`
        : null,
  });
}

export function useClientPriceDistributionChartData(params?: IFetchBaseParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IFetchBaseParams, IClientPriceDistChartItem>({
    endpoint:
      params?.slug && params?.tab
        ? `${endpoints.admin.clients}/${params?.slug}/${params?.tab}?${queryString}`
        : null,
  });
}

export function useGetClientDiscount(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IClientItem>({
    endpoint: params?.slug ? `${endpoints.admin.clients}/${params?.slug}/discount` : null,
    params,
  });
}

export function useGetClientRegions(params?: IFetchBaseParams) {
  return useGetData<unknown, IClientRegions>({
    endpoint: endpoints.admin.clientRegions,
    params,
  });
}

export function useGetClientRegionsProvinces(params?: IFetchBaseParams) {
  return useGetData<unknown, IClientRegionsAndProvinces>({
    endpoint: endpoints.admin.clientRegionsProvinces,
    params,
  });
}

export function useGetCountries(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, ICountriesResponse>({
    endpoint: endpoints.admin.countries,
    params,
  });
}

export function useGetAgentList(params?: IListFilters<IAgentsTableFilters>) {
  return useGetData<
    IListFilters<IAgentsTableFilters>,
    APIListResponse<IAgentItem[]> & { summary?: IAgentSummary }
  >({
    endpoint: endpoints.admin.agents,
    params,
  });
}

export function useGetAgentStatList(params?: IListFilters<IAgentsTableFilters>) {
  return useGetData<IListFilters<IAgentsTableFilters>, APIListResponse<IAgentItem[]>>({
    endpoint: endpoints.admin.agentsStat,
    params,
  });
}

export function useGetAgent(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IAgentItem>({
    endpoint: `${endpoints.admin.agents}/${params?.slug}`,
  });
}

// Agent commission "estratto conto" — matured (in) vs liquidated (out), by year.
export function useGetAgentAccountStatement({ slug, year }: { slug: string; year?: string }) {
  const queryString = new URLSearchParams({ year: year ?? 'all' }).toString();
  return useGetData<IFetchBaseParams, IAgentStatement>({
    endpoint: slug ? `${endpoints.admin.agents}/${slug}/account-statement?${queryString}` : null,
  });
}

export function useGetAgentCustomers(params?: IFetchBaseParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IFetchBaseParams, ICustomerOrders>({
    endpoint: params?.slug
      ? `${endpoints.admin.agents}/${params.slug}/${params.tab}?${queryString}`
      : null,
  });
}

export function useGetReservationList(params: IListFilters<IReservationsTableFilters>) {
  return useGetData<IListFilters<IReservationsTableFilters>, APIListResponse<IReservationItem[]>>({
    endpoint: endpoints.admin.reservations,
    params,
  });
}

export function useGetReservation(params: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IReservationItem>({
    endpoint: `${endpoints.admin.products.reservations(
      params?.sku as string,
      params?.client_slug as string,
    )}`,
  });
}

export function useGetAnnualStat(params?: IAnnualStatParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IAnnualStatParams, ISalesStatistics>({
    endpoint: params?.filter
      ? `${endpointsLegacy.admin.statistics.annualStat}/?${queryString}`
      : null,
  });
}
export function useGetWineTypeStat(params?: IAnnualStatParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IAnnualStatParams, WineRevenueStatistics[]>({
    endpoint: params?.filter
      ? `${endpointsLegacy.admin.statistics.wineType}/?${queryString}`
      : null,
  });
}

export function useGetYoyStats(params?: IAnnualStatParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IAnnualStatParams, ISalesStatistics>({
    endpoint: params?.filter
      ? `${endpointsLegacy.admin.statistics.getYoyStats}/?${queryString}`
      : null,
  });
}

export function useGetOrderPlacedByStats(params?: IAnnualStatParams) {
  const queryString = new URLSearchParams(params?.filter as Record<string, string>).toString();
  return useGetData<IAnnualStatParams, IOrderPlacedByStatistics>({
    endpoint: params?.filter
      ? `${endpoints.admin.statistics.getOrderPlacedByStats}/?${queryString}`
      : null,
  });
}

export function useProfitabilityStats(params: IProfitabilityRealTimeFilters) {
  return useGetData<IProfitabilityRealTimeFilters, YearlyStat>({
    endpoint: endpoints.admin.statistics.profitability,
    params,
  });
}

export function useGetSalesFlowStats(params: IFlowAnalysisFilters) {
  return useGetData<IFlowAnalysisFilters, IFlowAnalysisStats>({
    endpoint: endpoints.admin.statistics.sales_flow,
    params,
  });
}
export function useGetProductPerformanceList(params?: IProductPerformanceFilters) {
  return useGetData<IProductPerformanceFilters, IProductPerformanceStats>({
    endpoint: endpoints.admin.statistics.product_performance,
    params,
  });
}
export function useStatisticsSalesRegionwise(
  params?: IListFilters<IBestSellingProductsStatsFilter>,
) {
  return useGetData<IListFilters<IBestSellingProductsStatsFilter>, ISalesStatisticsRegionwise>({
    endpoint: endpointsLegacy.admin.statistics.saleStats,
    params,
    // ...isLegacy,
  });
}
export function useBestSellingStats(params?: IListFilters<IBestSellingProductsStatsFilter>) {
  return useGetData<IListFilters<IBestSellingProductsStatsFilter>, IBestSalesStatistics>({
    endpoint: endpointsLegacy.admin.statistics.bestSellingProductsStats,
    params,
    // ...isLegacy,
  });
}
export function useGetProductStats(params: IListFilters<IProductStatsFilters>) {
  return useGetData<IListFilters<IProductStatsFilters>, IProductStatsItem[]>({
    endpoint: endpoints.admin.statistics.productStats,
    params,
    // ...isLegacy,
  });
}
export function useGetProducerRisk(params: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IProducerRiskItem[]>({
    endpoint: endpoints.admin.statistics.producerRisk,
    params,
  });
}
export function useGetStockAnalysisOptimization(params: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IStockAnalysisOptimizationItem>({
    endpoint: `${endpoints.admin.products.root}/${params?.sku}/stats/stock_analysis_optimization`,
  });
}

export function useGetProfitability(params: IListFilters<IProfitabilityStatsFilters>) {
  return useGetData<IListFilters<IProfitabilityStatsFilters>, IProfitabilityItem>({
    endpoint: endpointsLegacy.admin.statistics.root,
    params,
    ...isLegacy,
  });
}

// Mutate API's

export async function createClient(data: IClientItem) {
  const URL = `${endpoints.admin.clients}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateClient(data: IClientItem) {
  const URL = `${endpoints.admin.clients}/${data?.slug}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteClient(slug: string) {
  const URL = `${endpoints.admin.clients}/${slug}`;
  await axiosInstance.delete(URL);
}

export async function createUser(data: IUserItem) {
  const URL = `${endpoints.admin.users}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateUser(data: IUserItem) {
  const URL = `${endpoints.admin.users}/${data?.slug}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteUser(slug: string) {
  const URL = `${endpoints.admin.users}/${slug}`;
  await axiosInstance.delete(URL);
}

export async function createReservation(data: IReservationItem) {
  const URL = `${endpoints.admin.products.reservation(data?.sku)}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateReservation(data: IReservationItem) {
  const URL = `${endpoints.admin.products.reservations(data?.sku, data?.client_slug)}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteReservation(client_slug: string, sku: string) {
  const URL = `${endpoints.admin.products.reservations(sku, client_slug)}`;
  await axiosInstance.delete(URL);
}

export async function createAgent(data: IAgentItem) {
  const URL = `${endpoints.admin.agents}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateAgent(data: IAgentItem) {
  const URL = `${endpoints.admin.agents}/${data?.slug}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteAgent(slug: string) {
  const URL = `${endpoints.admin.agents}/${slug}`;
  await axiosInstance.delete(URL);
}

export async function createProduct(data: IProductItem) {
  const URL = `${endpoints.admin.products.root}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateProduct(data: IProductItem) {
  const URL = `${endpoints.admin.products.root}/${data?.sku}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteProduct(sku: string) {
  const URL = `${endpoints.admin.products.root}/${sku}`;
  await axiosInstance.delete(URL);
}

export async function ImportProducts(data: IImportProductItem[]) {
  const URL = `${endpoints.admin.products.root}/storage_import`;
  const response = await axiosInstance.post(URL, data);
  return response?.data || {};
}

export async function createDiscount(data: IReservationItem) {
  const URL = `${endpoints.admin.products.discount(data?.sku)}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}
export async function deleteDiscount(data: { id: string; product_id: string }) {
  const URL = `${endpoints.admin.products.deleteDiscounts(data.id, data.product_id)}`;
  await axiosInstance.delete(URL);
}

export async function updateDiscount({
  client_slug,
  sku,
  discount,
}: {
  client_slug: string;
  sku: string;
  discount: number;
}) {
  const URL = `${endpoints.admin.clients}/${client_slug}/${endpoints.admin.client_discount}/${sku}`;
  const res = await axiosInstance.put(URL, { discount });
  return res.data;
}

export function useGetDiscountsList(params: IListFilters<IDiscountsTableFilters>) {
  return useGetData<IListFilters<IDiscountsTableFilters>, APIListResponse<IDiscountItem[]>>({
    endpoint: endpoints.admin.discounts,
    params,
  });
}

export async function createProducer(data: IProducerItem) {
  const URL = `${endpoints.admin.producers}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateProducer(data: IProducerItem) {
  const URL = `${endpoints.admin.producers}/${data?.slug}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteProducer(sku: string) {
  const URL = `${endpoints.admin.producers}/${sku}`;
  await axiosInstance.delete(URL);
}

/**
 * NAT-2562 — confirm the handle the scout pipeline proposed. By slug, as every other admin
 * producer route is. 409 when the producer has no handle (cleared elsewhere since the page
 * loaded).
 */
export async function confirmProducerInstagram(slug: string): Promise<IProducerInstagram> {
  const res = await axiosInstance.post(`${endpoints.admin.producers}/${slug}/instagram/confirm`);
  return res.data.data;
}

export function useGetProducerRegions(params?: IFetchBaseParams) {
  return useGetData<unknown, IClientRegions>({
    endpoint: endpoints.admin.producerRegions,
    params,
  });
}

export function useGetProducerRegionsProvinces(params?: IFetchBaseParams) {
  return useGetData<unknown, IClientRegionsAndProvinces>({
    endpoint: endpoints.admin.producerRegionsProvinces,
    params,
  });
}

export async function createProducerClientProvinceExclusions(
  data: IProducerClientProvinceExclusionsParams,
) {
  const URL = `${endpoints.admin.producers}/${data.slug}/producer_client_province_exclusions`;
  await axiosInstance.post(URL, data);
}

export async function deleteProducerClientProvinceExclusions(data: {
  region: string;
  province: string;
  slug: string;
}) {
  const URL = `${endpoints.admin.producers}/${data.slug}/producer_client_province_exclusions/${data.region}/${data.province}`;
  await axiosInstance.delete(URL);
}

export async function createClientOrder(data: IClientOrderItem) {
  const URL = `${endpoints.admin.orders.client_orders}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateClientOrder(data: IClientOrderItem) {
  const URL = `${endpoints.admin.orders.client_orders}/${data?.order_code}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

/**
 * Set one or more boolean flags on an order, leaving every other field untouched.
 *
 * Distinct from updateClientOrder, which takes the whole entity. The order-details
 * toggles used to echo the GET response back through that endpoint, so one switch
 * rewrote every field of the order; this sends only what changed.
 */
export async function updateClientOrderFlags(
  order_code: string,
  flags: Partial<Record<string, boolean>>,
) {
  const URL = `${endpoints.admin.orders.clientOrderFlags(order_code)}`;
  const res = await axiosInstance.patch(URL, flags);
  return res;
}

export async function deleteClientOrder(order_code: string) {
  const URL = `${endpoints.admin.orders.client_orders}/${order_code}`;
  await axiosInstance.delete(URL);
}

export async function createClientOrderProducts(order_code: string, data: ClientOrderedProducts) {
  const URL = `${endpoints.admin.orders.createClientOrderProduct(order_code)}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateClientOrderProducts(order_code: string, data: ClientOrderedProducts) {
  const URL = `${endpoints.admin.orders.updateClientOrderProduct(order_code, data?.id)}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteClientOrderProduct(order_code: string, id: string) {
  const URL = `${endpoints.admin.orders.updateClientOrderProduct(order_code, id)}`;
  await axiosInstance.delete(URL);
}

export async function createProducerOrder(data: IProducerOrderItem) {
  const URL = `${endpoints.admin.orders.producer_orders}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function createProducerOrderProducts(
  order_code: string,
  data: IProducerOrderProducts,
) {
  const URL = `${endpoints.admin.orders.producer_orders}/${order_code}/producer_order_products`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateProducerOrderProducts(
  order_code: string,
  data: IProducerOrderProducts,
) {
  const URL = `${endpoints.admin.orders.producer_orders}/${order_code}/producer_order_products/${data.id}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteProducerOrderProduct(order_code: string, id: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/${order_code}/producer_order_products/${id}`;
  await axiosInstance.delete(URL);
}

export async function updateProducerOrder(data: IProducerOrderItem) {
  const URL = `${endpoints.admin.orders.producer_orders}/${data?.order_code}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteProducerOrder(order_code: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/${order_code}`;
  await axiosInstance.delete(URL);
}

export async function notifyStorageAboutProducerOrder({
  order_code,
  preliminary,
}: INotifyStorageAboutProducerOrder) {
  const URL = `${endpoints.admin.orders.notifyStorageAboutProducerOrder(order_code)}`;
  const res = await axiosInstance.post(URL, preliminary ? { preliminary } : {});
  return res;
}

export async function notifyLogisticaAboutProducerOrder({
  order_id,
}: INotifyStorageAboutProducerOrder) {
  const URL = `${endpoints.admin.orders.notifyLogisticaAboutProducerOrder(order_id!)}`;
  const res = await axiosInstance.post(URL, {});
  return res;
}

export async function addAvailabilityToProducerOrder({
  order_code,
}: IAddAvailabilityToProducerOrder) {
  const URL = `${endpoints.admin.orders.addAvailability(order_code)}`;
  const response = await axiosInstance.put(URL);
  return response;
}

// Producer order send & reconciliation
export async function getLastCost(productId: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/last-cost?productId=${productId}`;
  const res = await axiosInstance.get(URL);
  return res.data?.data;
}

export async function sendOrderToProducer(orderCode: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/send`;
  const res = await axiosInstance.post(URL, {});
  return res;
}

export async function downloadOrderPdf(orderCode: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/pdf`;
  const res = await axiosInstance.get(URL, { responseType: 'blob' });
  return res;
}

export async function confirmProducerOrder(orderCode: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/confirm`;
  const res = await axiosInstance.post(URL, {});
  return res;
}

export async function reconcileProducerOrderItems(
  orderCode: string,
  items: Array<{ id: number; quantityReceived: number }>,
) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/reconcile`;
  const res = await axiosInstance.post(URL, { items });
  return res;
}

export async function cancelProducerOrder(orderCode: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/cancel`;
  const res = await axiosInstance.post(URL, {});
  return res;
}

export async function setProducerOrderStatus(orderCode: string, status: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/status`;
  const res = await axiosInstance.post(URL, { status });
  return res;
}

export async function resetReconciliation(orderCode: string, itemIds: number[]) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/reset-reconciliation`;
  const res = await axiosInstance.post(URL, { itemIds });
  return res;
}

// Supplier invoice + FiC suppliers (NAT-1956)
export async function createInvoiceUploadLink(orderCode: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/invoice/upload-link`;
  return axiosInstance.post(URL, { orderCode });
}

export async function extractInvoiceFields(orderCode: string, s3Key: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/invoice/extract`;
  return axiosInstance.post(URL, { s3Key });
}

export async function attachInvoice(
  orderCode: string,
  payload: {
    s3Key: string;
    invoiceNumber: string;
    invoiceDate: string;
    taxable: number;
    vat: number;
  },
) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/invoice`;
  return axiosInstance.post(URL, payload);
}

export async function downloadInvoice(orderCode: string) {
  const URL = `${endpoints.admin.orders.producer_orders}/${orderCode}/invoice/download`;
  return axiosInstance.get(URL, { responseType: 'arraybuffer' });
}

export async function searchFicSuppliers(q: string) {
  const URL = endpoints.admin.fic_suppliers;
  return axiosInstance.get(URL, { params: { q } });
}

export async function syncFicSuppliers() {
  const URL = `${endpoints.admin.fic_suppliers}/sync`;
  return axiosInstance.post(URL, {});
}

export function useGetLogisticsTypeValue() {
  return useGetData<null, string[]>({
    endpoint: endpoints.admin.statistics.logisticsTypeValues,
  });
}
export function useGetLogisticsSupplierList() {
  return useGetData<null, string[]>({
    endpoint: endpoints.admin.statistics.logisticsSupplierList,
  });
}

export async function deletelogisticsCost(id: string) {
  const URL = `${endpoints.admin.statistics.deleteLogisticsCost(id)}`;
  await axiosInstance.delete(URL);
}

export async function updateLogisticsCost(data: IUpdateLogisticsCost) {
  const URL = `${endpoints.admin.statistics.updateLogisticsCost}`;
  await axiosInstance.put(URL, data);
}

export async function recalculateStatistics(data: IRecalculateStatistics) {
  const URL = `${endpoints.admin.statistics.root}`;
  await axiosInstance.post(URL, data);
}

export async function notifyStorageAboutClientOrder({
  order_code,
  storage_notified,
  preliminary,
}: INotifyStorageAboutClientOrder) {
  const data: Pick<INotifyStorageAboutClientOrder, 'storage_notified' | 'preliminary'> = {};
  const URL = `${endpoints.admin.orders.notifyStorageAboutClientOrder(order_code!)}`;

  if (storage_notified) {
    data.storage_notified = storage_notified;
  }
  if (preliminary) {
    data.preliminary = preliminary;
  }
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function notifyLogisticaAboutClientOrder({
  order_id,
}: INotifyStorageAboutClientOrder) {
  const URL = `${endpoints.admin.orders.notifyLogisticaAboutClientOrder(order_id!)}`;
  const res = await axiosInstance.post(URL, {});
  return res;
}

export async function proFormaInvoiced({ order_code }: INotifyStorageAboutClientOrder) {
  const URL = `${endpoints.admin.orders.proFormaClientOrder(order_code!)}`;
  const res = await axiosInstance.post(URL, {});
  return res;
}

export async function createWineType(data: IWineTypeItem) {
  const URL = `${endpoints.admin.wineTypes}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateWineType(data: IWineTypeItem) {
  const URL = `${endpoints.admin.wineTypes}/${data?.id}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function createGrapeType(data: IGrapeTypeItem) {
  const URL = `${endpoints.admin.grapeTypes}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateGrapeType(data: IGrapeTypeItem) {
  const URL = `${endpoints.admin.grapeTypes}/${data?.id}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function createTastings(data: ITastingFormValues) {
  const URL = `${endpoints.admin.tastings}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateTasting(slug: string, data: ITastingFormValues) {
  return axiosInstance.put(`${endpoints.admin.tastings}/${slug}`, {
    place: data.place,
    tastingDate: data.tastingDate,
    notes: data.notes.trim() ? data.notes : null,
  });
}

export async function deleteTasting(slug: string) {
  return axiosInstance.delete(`${endpoints.admin.tastings}/${slug}`);
}

/** NAT-2894 — the backend builds the PDF and names the file; the caller saves it as sent. */
export async function downloadTastingPdf(params: { slug: string; clientSlug: string }) {
  const res = await axiosInstance.get(endpoints.admin.tastingPdf(params));
  return res.data?.data as { fileName: string; pdf: string };
}

export async function createTastingsClient(params: string, body: ITastingClient) {
  const URL = `${endpoints.admin.createClient(params)}`;
  const res = await axiosInstance.post(URL, body);
  return res;
}

export async function deleteTastingsClient(params: { slug: string; clientSlug: string }) {
  const URL = `${endpoints.admin.deleteClient(params)}`;
  const res = await axiosInstance.delete(URL);
  return res;
}

export async function createTastingsProduct(params: string, body: ITastingProduct) {
  const URL = `${endpoints.admin.createProduct(params)}`;
  const res = await axiosInstance.post(URL, body);
  return res;
}

export async function deleteTastingsProduct(params: { slug: string; sku: string }) {
  const URL = `${endpoints.admin.deleteProduct(params)}`;
  const res = await axiosInstance.delete(URL);
  return res;
}

export function useGetCourierList() {
  return useGetData<IListFilters<IClientTableFilters>, ICourier[]>({
    endpoint: endpoints.admin.courier,
  });
}
// overlay API's

export async function createOverlay(data: IOverlayItem) {
  const URL = `${endpoints.admin.overlay.root}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateOverlay(data: IOverlayItem) {
  const URL = `${endpoints.admin.overlay.root}/${data?.id}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteOverlay(overlayId: string) {
  const URL = `${endpoints.admin.overlay.root}/${overlayId}`;
  await axiosInstance.delete(URL);
}

export function useGetOverlayList() {
  return useGetData<IFetchBaseParams, IOverlayItem>({
    endpoint: `${endpoints.admin.overlay.root}`,
  });
}

export function useGetOverlay(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IOverlayItem>({
    endpoint: params?.overlayId ? `${endpoints.admin.overlay.root}/${params?.overlayId}` : null,
  });
}

export function useGetClientActiveOverlayForpdf() {
  return useGetData<Partial<IFetchBaseParams>, IOverlayItem>({
    endpoint: endpoints.admin.overlay.activePDF,
  });
}

export function useGetLogisticsStats(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, ILogisticsStats>({
    endpoint: `${endpoints.admin.overview.logistics}`,
    params,
  });
}

/**
 * NAT-2334 — `period` chooses the Termometro's span. It is part of the SWR key, so the two
 * spans cache separately and flipping the toggle back is instant rather than a refetch.
 *
 * Defaults to month-to-date and is ALWAYS sent, which is also what a backend that predates the
 * toggle answers with — so this page renders correctly during a deploy in either order.
 */
export function useGetCurrentMonthStats(period: DashboardPeriod = 'mtd') {
  return useGetData<{ period: DashboardPeriod }, ICurrentMonthStats>({
    endpoint: `${endpoints.admin.overview.currentMonthStats}`,
    // Always sent, never conditionally omitted: `[endpoint]` and `[endpoint, {params:{period:
    // 'mtd'}}]` hash to different SWR keys, so an omitted default would fetch and cache the
    // same month payload twice.
    params: { period },
    // The eight cards sit at the top of the page. Dropping to undefined mid-toggle would
    // unmount them and pull every section below up by their height.
    keepPreviousData: true,
  });
}

/**
 * DSO, DPO, weighted DIO and the cash conversion cycle, over the trailing 180 days.
 * NAT-2519: rendered by the "Ciclo di cassa" section of the Operativa dashboard.
 */
export function useGetCurrentQuarterStats() {
  return useGetData<IFetchBaseParams, ICurrentQuarterStats>({
    endpoint: `${endpoints.admin.overview.currentQuarterStats}`,
  });
}
export function useGetOptimizationTarget() {
  return useGetData<IFetchBaseParams, ITargetItem>({
    endpoint: `${endpoints.admin.overview.optimizationTarget}`,
  });
}

export async function createTarget(data: ITargetItem) {
  const URL = `${endpoints.admin.overview.target}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}
export function useStockOptimizationStats(params: IProfitabilityRealTimeFilters) {
  return useGetData<IProfitabilityRealTimeFilters, IStockOptimizationStat>({
    endpoint: endpoints.admin.statistics.stock_optimization,
    params,
  });
}
export function usePriceRangeStockBehaviourStats(params: IPriceRangeStockBehaviourFilters) {
  return useGetData<IPriceRangeStockBehaviourFilters, IPriceRangeStockBehaviourStats>({
    endpoint: endpoints.admin.statistics.price_range_stock_behaviour,
    params,
  });
}
export function usePriceRangeStockBehaviourStatsByPriceRange(
  params: IPriceRangeStockBehaviourFilters,
) {
  return useGetData<IPriceRangeStockBehaviourFilters, IPriceRangeStockBehaviourStats>({
    endpoint: endpoints.admin.statistics.single_range_stock_behaviour,
    params,
  });
}
export function useGetWineCategoriesData() {
  return useGetData<null, IWineCategoriesResponse>({
    endpoint: endpoints.admin.wineCategories,
  });
}

/**
 * DSO and DPO month by month, each measured at its month's last day (today, for the month in
 * progress). `year` is a calendar year, `last_12_months` or `previous_12_months`; omitted, it
 * defaults to the last 12 months. An unknown or future year is a 400.
 * NAT-2519: rendered by the "Ciclo di cassa" section of the Operativa dashboard.
 */
export function useGetFinancialYearlyTrends(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IYearlyTrends[]>({
    endpoint: `${endpoints.admin.overview.yearlyTrends}`,
    params,
  });
}

export function useGetAdministrationStats() {
  return useGetData<IFetchBaseParams, IOrderStats[]>({
    endpoint: `${endpoints.admin.overview.administration}`,
  });
}
export function useGetAgingReceivables() {
  return useGetData<IFetchBaseParams, IAgingReceivables>({
    endpoint: `${endpoints.admin.overview.agingReceivables}`,
  });
}

/**
 * NAT-2610 — «Da incassare»: today's receivables (with the chase list) and the last 24 months,
 * one response. The page and the Situazione tile both read it.
 *
 * NAT-2795, rule 52 — open to agents, who get their own clients only (the backend scopes by the
 * live user). `agentId` is an admin's filter, an agent id or `none` for «Direzionale»: it goes to
 * the backend and is part of the cache key. Called bare (the Situazione tile) the request carries
 * no query at all.
 */
export function useGetReceivablesPosition(agentId?: string) {
  return useGetData<{ agentId: string } | undefined, IReceivablesPosition>({
    endpoint: endpoints.admin.positions.receivables,
    params: agentId ? { agentId } : undefined,
    // No keepPreviousData: the page labels and exports these figures with the filter they were
    // asked under, so another filter's figures must never stand in while (or after) they load.
  });
}

/**
 * NAT-2612 — «Magazzino fermo»: today, the monthly history and the slow movers' population
 * totals, in one response the page and the Situazione tile both read. Admin only.
 */
export function useGetStockPosition() {
  return useGetData<IFetchBaseParams, IStockPosition>({
    endpoint: endpoints.admin.positions.stock,
  });
}

/**
 * NAT-2612 — one page of the slow movers, sorted and paged by the backend. Holds the previous
 * page while the next loads, so a sort or a page change does not collapse the table.
 */
export function useGetSlowMovers(params: ISlowMoversParams) {
  return useGetData<ISlowMoversParams, ISlowMoversPage>({
    endpoint: endpoints.admin.positions.slowMovers,
    params,
    keepPreviousData: true,
  });
}

/**
 * NAT-2654 — one page of the archive, sorted, searched and paged by the backend. Holds the
 * previous page while the next loads, so a sort or a page change does not collapse the table.
 */
export function useGetArchiveHoldList(params: IArchiveHoldListParams) {
  return useGetData<IArchiveHoldListParams, IArchiveHoldListResponse>({
    endpoint: endpoints.admin.archiveHolds.root,
    params,
    keepPreviousData: true,
  });
}

/** NAT-2654 — the product's hold (null when it has none) and its history, newest first. */
export function useGetProductArchiveHold(sku: string | undefined) {
  return useGetData<IFetchBaseParams, IProductArchiveHold>({
    endpoint: sku ? endpoints.admin.archiveHolds.product(sku) : null,
  });
}

/** NAT-2654 — create the hold, or set it to `quantity` (raising takes from available). */
export async function upsertArchiveHold(
  sku: string,
  payload: IArchiveHoldUpsertPayload,
): Promise<IArchiveHold> {
  const res = await axiosInstance.put<{ data: IArchiveHold }>(
    endpoints.admin.archiveHolds.product(sku),
    payload,
  );
  return res.data.data;
}

/** NAT-2654 — release bottles to open sale; `returned_to_sale` says how many storage took back. */
export async function releaseArchiveHold(
  sku: string,
  payload: IArchiveHoldReleasePayload,
): Promise<IArchiveHoldReleaseResult> {
  const res = await axiosInstance.post<{ data: IArchiveHoldReleaseResult }>(
    endpoints.admin.archiveHolds.release(sku),
    payload,
  );
  return res.data.data;
}

/** NAT-2654 — «Assegna a cliente»: move bottles from the hold into the client's reservation. */
export async function allocateArchiveHold(
  sku: string,
  payload: IArchiveHoldAllocatePayload,
): Promise<IArchiveHoldAllocateResult> {
  const res = await axiosInstance.post<{ data: IArchiveHoldAllocateResult }>(
    endpoints.admin.archiveHolds.allocate(sku),
    payload,
  );
  return res.data.data;
}

/**
 * NAT-2864 — «Qualità stock»: the headline, the verdict counts and cards 352/353, for the
 * filters. Admin only.
 */
export function useGetStockQualitySummary(params: StockQualityFilterParams) {
  return useGetData<StockQualityFilterParams, IStockQualitySummary>({
    endpoint: endpoints.admin.stockQuality.summary,
    params,
  });
}

/** NAT-2864 — one page of wines with their verdict, sorted and paged by the backend. */
export function useGetStockQualityWines(params: IStockQualityListParams<StockWineSortKey>) {
  return useGetData<IStockQualityListParams<StockWineSortKey>, IStockWinesPage>({
    endpoint: endpoints.admin.stockQuality.wines,
    params,
    keepPreviousData: true,
  });
}

/** NAT-2864 — one page of producers with their verdict counts, sorted and paged by the backend. */
export function useGetStockQualityProducers(params: IStockQualityListParams<StockProducerSortKey>) {
  return useGetData<IStockQualityListParams<StockProducerSortKey>, IStockProducersPage>({
    endpoint: endpoints.admin.stockQuality.producers,
    params,
    keepPreviousData: true,
  });
}

/**
 * NAT-1713 — log a collection activity.
 *
 * The backend denormalises status and last-contacted onto the client inside the same
 * transaction, so the caller refetches the aging report rather than patching a row locally:
 * the status it would have to guess is decided server-side by a ladder, and a client already
 * further along is deliberately not moved back.
 */
export async function createCollectionActivity(payload: ICreateCollectionActivity) {
  const res = await axiosInstance.post(endpoints.admin.collectionActivities.root, payload);
  return res;
}

/** Passing a null clientId skips the request — the history is only fetched when expanded. */
export function useGetCollectionActivities(clientId: number | null) {
  return useGetData<IFetchBaseParams, ICollectionActivity[]>({
    endpoint: clientId ? endpoints.admin.collectionActivities.byClient(clientId) : null,
  });
}

// NAT-2523 — the retention work-list. The backend decides who sees which clients.
//
// No `keepPreviousData`: every parameter here changes what the counts describe (the agent
// changes the scope, the view and reason change the rows), so a previous key's data shown
// while the next one loads would be another query's numbers under this query's tab.
export function useGetRetentionList(params: IRetentionListParams) {
  return useGetData<IRetentionListParams, IRetentionList>({
    endpoint: endpoints.admin.retention.root,
    params,
  });
}

/** Reassigning is admin-only, so agents pass `enabled: false` and never ask. */
export function useGetRetentionOwners(enabled: boolean) {
  return useGetData<IFetchBaseParams, IRetentionOwnerCandidate[]>({
    endpoint: enabled ? endpoints.admin.retention.owners : null,
  });
}

export async function updateRetentionEntry(clientId: number, payload: IRetentionUpdate) {
  const res = await axiosInstance.put(endpoints.admin.retention.entry(clientId), payload);
  return res;
}

export function useGetAgingPayables() {
  return useGetData<IFetchBaseParams, IAgingPayables>({
    endpoint: `${endpoints.admin.overview.agingPayables}`,
  });
}

export async function sendExciseReportEmail({ date }: { date: string }) {
  const res = await axiosInstance.post(endpoints.admin.overview.exciseReportEmail, { date });
  return res;
}
export async function sendClientPdfLink({ slug }: { slug: string }) {
  const URL = `${endpoints.admin.clients}/${slug}/pdf/email/send`;
  const res = await axiosInstance.post(URL);
  return res;
}
// create agent commission
export async function createAgentCommission(data: commissionItem) {
  const URL = `${endpoints.admin.agents}/${data.agentId}/yearly-commission`;
  const res = await axiosInstance.post(URL, { date: data.date, amount: data.amount });

  return res;
}
// get agent commission
export function useGetAgentCommission({
  agentId,
  sortOrder,
}: {
  agentId: number;
  sortOrder: string;
}) {
  const queryString = new URLSearchParams({
    sortOrder: sortOrder ?? '',
  }).toString();
  return useGetData<IFetchBaseParams, commissionItemData>({
    endpoint: `${endpoints.admin.agents}/${agentId}/yearly-commission?${queryString}`,
  });
}
// delete agent commission
export async function deleteAgentCommission({ id, agentId }: { id: string; agentId: string }) {
  const queryString = new URLSearchParams({
    id: id ?? null,
  }).toString();
  const URL = `${endpoints.admin.agents}/${agentId}/yearly-commission?${queryString}`;

  await axiosInstance.delete(URL);
}

// create agent service zone
export async function createAgentserviceZone({
  agentId,
  data,
}: {
  agentId: string;
  data: IServiceZoneItem;
}) {
  const payload = {
    region_id: data?.region,
    province_ids: data?.provinces,
  };

  const URL = `${endpoints.admin.agentServiceZone}/${agentId}`;
  const res = await axiosInstance.post(URL, payload);

  return res;
}
// get agent service zone
export function useGetAgentServiceZone({ agentId }: { agentId: string | number }) {
  return useGetData<IFetchBaseParams, IAgentServiceZone[]>({
    endpoint: agentId ? `${endpoints.admin.agentServiceZone}/${agentId}` : null,
  });
}
export async function deleteAgentServiceZone({
  agentId,
  regionId,
}: {
  agentId: string;
  regionId: string;
}) {
  const URL = `${endpoints.admin.agentServiceZone}/${agentId}/${regionId}`;
  await axiosInstance.delete(URL);
}

export function useGetProducerStock(slug?: string) {
  return useGetData<undefined, IProducerStockItem[]>({
    endpoint: slug ? `${endpoints.admin.producers}/${slug}/stock` : null,
  });
}
// all table texts (explanations)
export function useGetAllTableTexts() {
  return useGetData<undefined, ITableTextItem[]>({
    endpoint: endpoints.admin.tableText,
  });
}

/**
 * NAT-2430 — the assistant's daily metric rollup.
 *
 * `keepPreviousData` because the only thing that changes the key here is the
 * period toggle: without it the whole panel unmounts and the page jumps every
 * time someone switches 7/30/90.
 *
 * `metrics` is sent explicitly rather than left to the BE default so the
 * request is self-describing; unknown keys are dropped server-side rather than
 * rejected, which is what makes a BE-then-FE deploy order safe.
 */
/**
 * NAT-2431 — the anonymous conversation list.
 *
 * Nothing identifying comes back: a person is an alias. `keepPreviousData`
 * because the only thing that changes the key is a filter, and the list
 * vanishing under the filter bar is worse than it dimming.
 */
export function useGetAssistantConversations(params: {
  days: number;
  mode?: string;
  unserved?: boolean;
  thumbsDown?: boolean;
}) {
  return useGetData<Record<string, string>, IAssistantConversationList>({
    endpoint: endpoints.admin.assistantConversations,
    params: {
      days: String(params.days),
      ...(params.mode ? { mode: params.mode } : {}),
      ...(params.unserved ? { unserved: 'true' } : {}),
      ...(params.thumbsDown ? { thumbsDown: 'true' } : {}),
    },
    keepPreviousData: true,
  });
}

/** One conversation in full. `null` id means "nothing selected", so SWR skips. */
export function useGetAssistantConversation(id: string | null) {
  return useGetData<undefined, IAssistantConversationDetail>({
    endpoint: id ? endpoints.admin.assistantConversation(id) : null,
  });
}

/**
 * NAT-2431 — the questions the assistant could not serve.
 *
 * A SEPARATE request from the conversations above, deliberately: this store is
 * de-identified by construction and must never share a payload assembled from a
 * join with the identified side.
 */
export function useGetAssistantUnserved(days: number) {
  return useGetData<Record<string, string>, IAssistantUnservedList>({
    endpoint: endpoints.admin.assistantUnserved,
    params: { days: String(days) },
    keepPreviousData: true,
  });
}

export function useGetAssistantMetrics(days: number) {
  return useGetData<{ days: number; metrics: string }, IAssistantMetricsResponse>({
    endpoint: endpoints.admin.assistantMetrics,
    params: { days, metrics: REQUESTED_METRICS.join(',') },
    keepPreviousData: true,
  });
}

/**
 * NAT-2524 — the reorder sheet. `statuses` narrows it server-side, so the summary
 * and the producer list always describe the same set of wines; no statuses means
 * all four. Keeps the previous sheet on screen while a filter chip swaps it.
 */
export function useGetReorderSheet(statuses?: ReorderSheetStatus[]) {
  return useGetData<{ status: string } | undefined, ReorderSheet>({
    endpoint: endpoints.admin.reorderSheet,
    params: statuses?.length ? { status: statuses.join(',') } : undefined,
    keepPreviousData: true,
  });
}

/**
 * Cash due in over the next 7 days and out over the next 30. NAT-2519: both sides now read the
 * same aging rows as the "Da incassare" and "Da pagare" sections, so `totalToReceive` /
 * `totalToPay` and `overdue` agree with those sections to the cent — this is the open balance,
 * not a full-order approximation of it. Rendered by the "Ciclo di cassa" section of the
 * Operativa dashboard.
 */
export function useGetIncomingOutgoingCashflowData() {
  return useGetData<undefined, IIncomingOutgoingCashflowItem>({
    endpoint: endpoints.admin.overview.incomingOutgoingCashflow,
  });
}

// Get one table text by key
export function useGetTableTextByKey(key?: string) {
  return useGetData<undefined, ITableTextItem>({
    endpoint: key ? endpoints.admin.tableTextByKey(key) : null,
  });
}

// Get table explanation (may be slightly different structure, if used separately)
export function useGetTableExplanation(params?: ITableTextParams) {
  return useGetData<ITableTextParams, ITableExplanation>({
    endpoint: params?.key ? endpoints.admin.tableTextByKey(params.key) : null,
    params,
  });
}

//  Update a table explanation (still needs axiosInstance as it's a PUT request)
export async function updateTableExplanation({
  key,
  content,
}: {
  key: string;
  content: string;
}): Promise<void> {
  return axiosInstance.put(endpoints.admin.tableTextByKey(key), { content });
}

// Financial data API
export function useGetFinancialData() {
  return useGetData<undefined, IFinancialResponse>({
    endpoint: endpoints.admin.overview.financials,
  });
}

// GET: List producer payments
export function useGetProducerPayments(params: { producerId: string }) {
  return useGetData<{ producerId: string }, IProducerPayment[]>({
    endpoint: `${endpoints.admin.producer_payments}/${params.producerId}`,
  });
}

// POST: Create new payment
export async function createProducerPayment(payment: IProducerPaymentCreate) {
  const URL = endpoints.admin.producer_payments;
  const response = await axiosInstance.post(URL, payment);
  return response.data;
}

// DELETE: Delete payment by ID
export async function deletePaymentById(paymentId: string): Promise<void> {
  const URL = endpoints.admin.deleteProducerPayment(paymentId);
  await axiosInstance.delete(URL);
}

// GET: List producer dues
export function useGetProducerDues() {
  return useGetData<void, ProducerDueItem[]>({
    endpoint: endpoints.admin.producer_dues,
  });
}

// GET: Fetch target by key
export async function getTargetByKey(targetKey: string) {
  const URL = `${endpoints.admin.targets}/${targetKey}`;
  const response = await axiosInstance.get(URL);
  return response.data.data;
}

// GET: Weekly sales performance overview
export async function getWeeklySalesPerformance(): Promise<FinancialOverviewApiResponse> {
  const URL = `${endpoints.admin.overview.weeklySalesPerformance}`;
  const response = await axiosInstance.get(URL);
  return response.data.data;
}

/**
 * The daily stock and customer KPIs. NAT-2522: only `capitalTiedUp120` and `zeroSales60` are
 * rendered (the Operativa stock section). `zeroSales60` now uses the real sale definition and
 * counts only stocked products (NAT-2525), and is the single source for that count — the
 * section's product list is read separately, for its rows, not for this figure.
 */
export function useGetDailyKpiSummary() {
  return useGetData<undefined, DailyKpiSummary>({
    endpoint: endpoints.admin.overview.getKpiSummary,
  });
}

// ----------------------------------------------------------------------
// TERRITORY API FUNCTIONS
// ----------------------------------------------------------------------

export function useGetTerritoryList(params?: IListFilters<ITerritoryTableFilters>) {
  const response = useGetData<IListFilters<ITerritoryTableFilters>, any>({
    endpoint: '/admin/territories',
    params,
  });

  const territories: ITerritoryItem[] = response.data?.data?.territories || [];
  const totalRows: number = response.data?.data?.pagination?.total || 0;

  return {
    ...response,
    data: territories,
    totalRows,
  };
}

export function useGetTerritory(params?: IFetchBaseParams) {
  const identifier = params?.id || params?.slug;
  return useGetData<IFetchBaseParams, ITerritoryResponse>({
    endpoint: `${endpoints.admin.territories}/${identifier}`,
  });
}

export async function createTerritory(data: Partial<ITerritoryItem>) {
  const URL = `${endpoints.admin.territories}`;
  const res = await axiosInstance.post(URL, data);
  return res;
}

export async function updateTerritory(identifier: string, data: Partial<ITerritoryItem>) {
  const URL = `${endpoints.admin.territories}/${identifier}`;
  const res = await axiosInstance.put(URL, data);
  return res;
}

export async function deleteTerritory(identifier: string) {
  const URL = `${endpoints.admin.territories}/${identifier}`;
  await axiosInstance.delete(URL);
}

export function useGetProducerRegionsList(params?: IFetchBaseParams) {
  return useGetData<IFetchBaseParams, IProducerRegionItem[]>({
    endpoint: endpoints.admin.producerRegions,
    params,
  });
}

// Countries API - for admin country dropdown in producer form
export function useGetCountriesList(params?: IFetchBaseParams) {
  const response = useGetData<IFetchBaseParams, ICountryItem[]>({
    endpoint: endpoints.admin.countries,
    params,
  });

  const countries: ICountryItem[] = response.data || [];

  return {
    ...response,
    data: { countries },
  };
}

// LogisticaIT Sync API
export async function syncLogisticaProducers() {
  return axiosInstance.post(endpoints.admin.logisticaIt.syncProducers);
}

export async function syncLogisticaProducts() {
  return axiosInstance.post(endpoints.admin.logisticaIt.syncProducts);
}

export async function syncLogisticaClients() {
  return axiosInstance.post(endpoints.admin.logisticaIt.syncClients);
}

export async function syncLogisticaStock() {
  return axiosInstance.post(endpoints.admin.logisticaIt.syncStock);
}

// Fatture In Cloud VAT Sync API
export async function syncFicVatData() {
  return axiosInstance.post(endpoints.admin.ficSync.syncVat);
}

// Catalog PDF (NAT-1766)
export function useGetCatalogPdfJobs(limit: number = 10) {
  return useGetData<{ limit: number }, ICatalogPdfJob[]>({
    endpoint: endpoints.admin.catalogPdf.jobs,
    params: { limit },
  });
}

export async function generateCatalogPdf() {
  const res = await axiosInstance.post(endpoints.admin.catalogPdf.generate);
  return res.data?.data as ICatalogPdfJob;
}

export async function getCatalogPdfJobStatus(jobId: string) {
  const res = await axiosInstance.get(endpoints.admin.catalogPdf.status(jobId));
  return res.data?.data as ICatalogPdfJob;
}

// Product Selections

export function useGetSelectionsList(params?: IListFilters<{}>) {
  return useGetData<IListFilters<{}>, ISelectionItem[]>({
    endpoint: endpoints.admin.productSelections.root,
    params,
  });
}

export async function getSelection(id: string | number) {
  const res = await axiosInstance.get(endpoints.admin.productSelections.byId(id));
  return res.data?.data as ISelectionItem;
}

export async function createSelection(data: Omit<ISelectionItem, 'id' | 'count'>) {
  const res = await axiosInstance.post(endpoints.admin.productSelections.root, data);
  return res;
}

export async function updateSelection(id: string | number, data: Partial<ISelectionItem>) {
  const res = await axiosInstance.put(endpoints.admin.productSelections.byId(id), data);
  return res;
}

export async function archiveSelection(id: string | number) {
  await axiosInstance.delete(endpoints.admin.productSelections.byId(id));
}

export async function setSelectionItems(id: string | number, productIds: number[]) {
  const res = await axiosInstance.put(endpoints.admin.productSelections.items(id), { productIds });
  return res;
}

// ----------------------------------------------------------------------
// NAT-2027 — the two ledger lenses (Aziende + client customer-360)

export function useGetCompaniesList(params?: { search?: string; page?: number; limit?: number }) {
  return useGetData<{ search?: string; page?: number; limit?: number }, ICompanyListResponse>({
    endpoint: endpoints.admin.companies,
    params,
  });
}

export function useGetProducerCompanies(params?: {
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useGetData<
    { search?: string; page?: number; limit?: number },
    IProducerCompanyListResponse
  >({
    endpoint: endpoints.admin.producerCompanies,
    params,
  });
}

export function useGetProducerCompany(ficEntityId?: string) {
  return useGetData<undefined, IProducerCompany360>({
    endpoint: ficEntityId ? `${endpoints.admin.producerCompanies}/${ficEntityId}` : null,
  });
}

export function useGetProducerFicEntities(params?: { search?: string }) {
  return useGetData<{ search?: string }, IProducerFicEntityListResponse>({
    endpoint: endpoints.admin.producerFicEntities,
    params,
  });
}

export function useGetCompany(companyId?: string) {
  return useGetData<undefined, ICompany360>({
    endpoint: companyId ? `${endpoints.admin.companies}/${companyId}` : null,
  });
}

export function useGetCustomer360(clientId?: string | number) {
  return useGetData<undefined, ICustomer360>({
    endpoint: clientId ? `${endpoints.admin.clients}/${clientId}/customer-360` : null,
  });
}
